import type { DispatchUserMessageArgs } from "@/hooks/prompt-dispatch-types";

const STORAGE_KEY = "denker.prompt-retry-envelopes.v1";
const ENVELOPE_TTL_MS = 10 * 60_000;
const MAX_ENVELOPES = 20;
const MAX_STORAGE_BYTES = 256_000;

export interface PromptRetryEnvelope {
  version: 1;
  composerId: string;
  draftText: string;
  args: DispatchUserMessageArgs;
  clientRequestId?: string;
  createdAt: number;
  expiresAt: number;
}

const envelopes = new Map<string, PromptRetryEnvelope>();

function isEnvelope(
  value: unknown,
  now = Date.now(),
): value is PromptRetryEnvelope {
  if (!value || typeof value !== "object") return false;
  const envelope = value as Partial<PromptRetryEnvelope>;
  const args = envelope.args as Partial<DispatchUserMessageArgs> | undefined;
  return (
    envelope.version === 1 &&
    typeof envelope.composerId === "string" &&
    typeof envelope.draftText === "string" &&
    !!args &&
    typeof args.text === "string" &&
    !!args.position &&
    Number.isFinite(args.position.x) &&
    Number.isFinite(args.position.y) &&
    (envelope.clientRequestId === undefined ||
      typeof envelope.clientRequestId === "string") &&
    typeof envelope.createdAt === "number" &&
    typeof envelope.expiresAt === "number" &&
    envelope.expiresAt > now
  );
}

function prune(now = Date.now()): void {
  for (const [composerId, envelope] of envelopes) {
    if (!isEnvelope(envelope, now)) envelopes.delete(composerId);
  }
  [...envelopes.values()]
    .sort((first, second) => second.createdAt - first.createdAt)
    .slice(MAX_ENVELOPES)
    .forEach((envelope) => envelopes.delete(envelope.composerId));
}

function persist(): void {
  if (typeof window === "undefined") return;
  prune();
  try {
    const serialized = JSON.stringify([...envelopes.values()]);
    if (serialized.length <= MAX_STORAGE_BYTES) {
      localStorage.setItem(STORAGE_KEY, serialized);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Exact retry remains available in this webview when storage is blocked.
  }
}

function hydrate(): void {
  if (typeof window === "undefined" || envelopes.size > 0) return;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw || raw.length > MAX_STORAGE_BYTES) return;
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return;
    for (const candidate of parsed) {
      if (isEnvelope(candidate)) envelopes.set(candidate.composerId, candidate);
    }
    prune();
  } catch {
    // Invalid or inaccessible persistence is ignored.
  }
}

export function retainPromptRetryEnvelope({
  composerId,
  draftText,
  args,
  clientRequestId,
}: {
  composerId: string;
  draftText: string;
  args: DispatchUserMessageArgs;
  clientRequestId?: string;
}): void {
  const now = Date.now();
  envelopes.set(composerId, {
    version: 1,
    composerId,
    draftText: draftText.trim(),
    args,
    clientRequestId,
    createdAt: now,
    expiresAt: now + ENVELOPE_TTL_MS,
  });
  persist();
}

export function promptRetryEnvelopeForDraft(
  composerId: string,
  draftText: string,
): PromptRetryEnvelope | null {
  hydrate();
  prune();
  const envelope = envelopes.get(composerId);
  return envelope?.draftText === draftText.trim() ? envelope : null;
}

export function clearPromptRetryEnvelopesForComposer(composerId: string): void {
  hydrate();
  envelopes.delete(composerId);
  persist();
}

export function clearPromptRetryEnvelopeForRequest(requestId: string): void {
  hydrate();
  for (const [composerId, envelope] of envelopes) {
    if (envelope.clientRequestId === requestId) envelopes.delete(composerId);
  }
  persist();
}

export function bindPromptRetryEnvelopeRequest(
  composerId: string,
  requestId: string,
): void {
  hydrate();
  const envelope = envelopes.get(composerId);
  if (!envelope) return;
  envelopes.set(composerId, { ...envelope, clientRequestId: requestId });
  persist();
}

export function resetPromptRetryEnvelopesForTests(): void {
  envelopes.clear();
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Tests may install a throwing Storage implementation.
  }
}
